/**
 * @module ReasoningAiService
 * @purpose API client functions for the Part C AI summary and provider-compare endpoints.
 * @depends apiClient (services/api.ts), reasoning types (types/reasoning.ts)
 * @dependedOnBy hooks/use-cluster-briefing.ts, hooks/use-provider-briefing.ts
 *
 * @rules
 * - Performance: stateless service functions; state lives in hooks
 * - State: stateless
 * - Errors: ApiError propagates from apiClient — no try-catch swallowing;
 *     503 (isServiceUnavailable) is caught by the hook and triggers deterministic fallback;
 *     400 provider_not_configured is surfaced by the hook as a clear message (FR8)
 */

import { apiClient } from './api'
import type {
  ClusterAiSummary,
  HashResolution,
  AllProviderBriefings,
  BriefingJob,
  GenerateBriefingRequest,
  GenerateBriefingResponse,
  BoardWideGenerateRequest,
  BoardWideGenerateResponse,
} from '../types/reasoning'

const HTTP_NOT_FOUND = 404
const HTTP_METHOD_NOT_ALLOWED = 405

/**
 * Fetch the Part C AI summary for a single cluster.
 *
 * @behaviour Throws ApiError on failure; callers treat 503 (isServiceUnavailable)
 *   as an AI-absent signal and render the deterministic headline fallback instead.
 * @errors Propagates ApiError from apiClient (401, 403, 404, 500, 503).
 */
export async function getClusterAiSummary(
  caseId: string,
  runId: string,
  clusterId: string,
): Promise<ClusterAiSummary> {
  return apiClient.get<ClusterAiSummary>(
    `/cases/${caseId}/reasoning-runs/${runId}/clusters/${clusterId}/ai-summary`,
  )
}

/**
 * Batch-warm the top-N cluster AI summaries in a single request.
 *
 * @behaviour Used by useBriefingBoard to warm the board in one bounded POST rather
 *   than N individual calls.  On 503 the hook degrades all tiles to deterministic fallback.
 * @errors Propagates ApiError from apiClient (401, 403, 404, 500, 503).
 */
export async function batchClusterAiSummaries(
  caseId: string,
  runId: string,
  topN: number,
): Promise<{ generated: number; cached: number; summaries: ClusterAiSummary[] }> {
  return apiClient.post(
    `/cases/${caseId}/reasoning-runs/${runId}/clusters/ai-summaries`,
    { topN },
  )
}

// =============================================================================
// Provider-compare API functions (FR4–FR7)
// =============================================================================

interface GenerateBriefingParams {
  caseId: string
  runId: string
  clusterId: string
  body: GenerateBriefingRequest
}

/**
 * Start an async briefing generation job for a single cluster.
 *
 * @behaviour Returns immediately with a jobId + status; client must poll pollBriefingJob.
 *   On 409 (DuplicateJob) ApiError.isConflict is true.
 *   On 400 provider_not_configured, the hook surfaces a clear "not configured" message.
 * @errors Propagates ApiError from apiClient (400, 401, 403, 404, 409, 429, 500).
 */
export async function generateBriefing(
  params: GenerateBriefingParams,
): Promise<GenerateBriefingResponse> {
  const { caseId, runId, clusterId, body } = params
  return apiClient.post<GenerateBriefingResponse>(
    `/cases/${caseId}/reasoning-runs/${runId}/clusters/${clusterId}/ai-summary/generate`,
    body,
  )
}

interface PollBriefingJobParams {
  caseId: string
  runId: string
  jobId: string
}

/**
 * Poll the status of a briefing generation job.
 *
 * @behaviour Returns the current job state. Call until status is 'succeeded' or 'failed'.
 *   Never returns API keys.
 * @errors Propagates ApiError from apiClient (401, 403, 404, 500).
 */
export async function pollBriefingJob(params: PollBriefingJobParams): Promise<BriefingJob> {
  const { caseId, runId, jobId } = params
  return apiClient.get<BriefingJob>(
    `/cases/${caseId}/reasoning-runs/${runId}/ai-summary/jobs/${jobId}`,
  )
}

interface GetAllProviderBriefingsParams {
  caseId: string
  runId: string
  clusterId: string
}

/**
 * Fetch all cached provider briefings for a cluster (FR4 — compare read).
 *
 * @behaviour Returns an empty briefings array when nothing is cached — not a 404.
 *   guardProvenance may be null for pre-guard rows.
 * @errors Propagates ApiError from apiClient (401, 403, 404, 500).
 */
export async function getAllProviderBriefings(
  params: GetAllProviderBriefingsParams,
): Promise<AllProviderBriefings> {
  const { caseId, runId, clusterId } = params
  return apiClient.get<AllProviderBriefings>(
    `/cases/${caseId}/reasoning-runs/${runId}/clusters/${clusterId}/ai-summaries`,
  )
}

interface BoardWideGenerateParams {
  caseId: string
  runId: string
  body: BoardWideGenerateRequest
}

/**
 * Start board-wide async briefing generation for the top-N clusters.
 *
 * @behaviour Returns a job per cluster + a conservative estimateSeconds for the ETA confirm.
 *   Capped at MAX_BATCH (20) server-side. 400 on unconfigured provider.
 * @errors Propagates ApiError from apiClient (400, 401, 403, 404, 429, 500).
 */
export async function boardWideGenerate(
  params: BoardWideGenerateParams,
): Promise<BoardWideGenerateResponse> {
  const { caseId, runId, body } = params
  return apiClient.post<BoardWideGenerateResponse>(
    `/cases/${caseId}/reasoning-runs/${runId}/clusters/ai-summaries/generate`,
    body,
  )
}

interface ResolveHashConflictParams {
  caseId: string
  runId: string
  clusterId: string
  resolution: HashResolution
}

/**
 * Submit an analyst hash-conflict resolution choice to the backend.
 *
 * @behaviour POSTs the resolution to the hash-resolution endpoint. 404 and 405 are
 *   tolerated defensively (transport guard for unexpected routing gaps). Any other
 *   error — including 422 validation errors such as fabricated_hash or no_conflict —
 *   is re-thrown so the caller can surface it as an inline error.
 * @errors Re-throws ApiError for non-404/405 statuses.
 */
export async function resolveHashConflict(params: ResolveHashConflictParams): Promise<void> {
  const { caseId, runId, clusterId, resolution } = params
  try {
    await apiClient.post(
      `/cases/${caseId}/reasoning-runs/${runId}/clusters/${clusterId}/hash-resolution`,
      resolution,
    )
  } catch (error) {
    const status =
      error instanceof Error && 'status' in error
        ? (error as { status: number }).status
        : null
    if (status === HTTP_NOT_FOUND || status === HTTP_METHOD_NOT_ALLOWED) {
      return
    }
    throw error
  }
}
